import React, { useEffect, useReducer, useState } from 'react';

import { VulnerabilitySeverity } from '@ariksa/inventory-core/api';
import { Box } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { FunnelStep } from 'services/Vulnerability/types';

import { CDashboard } from 'components/Visualization';
import {
  IDashboardLayout,
  WidgetProps,
} from 'components/Visualization/CDashboard/types';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { HostsWithUnpatchedOSAndTechnology } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/HostsWithUnpatchedOSAndTechnology';
import { HostsWithVulnerabilities } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/HostsWithVulnerabilities';
import { MostDeployedVulnerableImages } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/MostDeployedVulnerableImages';
import { PatchablePackages } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/PatchablePackages';
import { PrioritizationFlowWidget } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/PrioritizationFlow';
import { VulnerabilitiesBySeverity } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/VulnerabilitiesBySeverity';
import { VulnerabilitiesBySeverityChart } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/VulnerabilitiesBySeverityChart';
import { VulnerabilitiesBySources } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/VulnerabilitiesBySources';
import { VulnerabilitiesPrioritization } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/VulnerabilitiesPrioritization';
import { vulnerabilityChartItems } from 'containers/Visibility/Vulnerabilities/Components/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';

import { actions } from '../../slice';

interface Props {
  setShow: (val: string) => void;
  useVulnerabilityV3?: boolean;
}

export const VulnerabilityDashboard: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const { environmentId, accountId } = useAccessBoundary();
  const configuredVulnerabilityV3 = props.useVulnerabilityV3;
  const [activeStepIds, setActiveStepIds] = useState<string[]>([
    FunnelStep.VulnerablePackages,
    FunnelStep.InternetExposedPackages,
    FunnelStep.PackagesWithKnownExploits,
    FunnelStep.PatchablePackages,
    FunnelStep.HostsToPatch,
  ]);
  const {
    useVulnerabilityV3: stateVulnerabilityV3,
    openVulnerabilitiesBySeverity,
    resolvedVulnerabilitiesBySeverity,
    hostsWithOpenVulnerabilities,
    hostsWithResolvedVulnerabilities,
    hostsWithOpenVulnerabilitiesByType,
    hostsWithResolvedVulnerabilitiesByType,
  } = useSelector(selectVulnerabilities);
  const useVulnerabilityV3 = configuredVulnerabilityV3 ?? stateVulnerabilityV3;
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      hostChartType: vulnerabilityChartItems[0],
      resolvedHostChartType: vulnerabilityChartItems[0],
      technologyChartType: vulnerabilityChartItems[0],
      resolvedTechnologyChartType: vulnerabilityChartItems[0],
      vulnerabilityChartType: vulnerabilityChartItems[0],
      resolvedVulnerabilityChartType: vulnerabilityChartItems[0],
    },
  );

  useEffect(() => {
    const dates = {
      startDate: dayjs().subtract(1, 'week').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    };
    const payload = {
      environmentId,
      accountId: !!accountId ? [accountId] : [],
      ...dates,
    };
    if (useVulnerabilityV3) {
      dispatch(actions.getScanVaultDashboard({ q: payload }));
      return;
    }
    dispatch(
      actions.getVulnerabilitiesBySeverityCounts({
        q: {
          ...payload,
        },
      }),
    );
    dispatch(
      actions.getVulnerabilitiesBySources({
        q: {
          environmentId,
          accountId: !!accountId ? [accountId] : [],
        },
      }),
    );
    dispatch(
      actions.getMostDeployedVulnerableImages({
        q: {
          environmentId,
          //accountId: !!accountId ? [accountId] : [],
        },
      }),
    );
    dispatch(
      actions.getPatchablePackages({
        q: {
          environmentId,
          accountId: !!accountId ? [accountId] : [],
          isPatchable: true,
          page: 1,
          size: 20,
          severity: [
            VulnerabilitySeverity.Critical,
            VulnerabilitySeverity.High,
          ],
          resolved: false,
          /*sortBy: 'severity',
          sortOrder: 'desc',*/
        },
      }),
    );
  }, [dispatch, environmentId, accountId, useVulnerabilityV3]);

  // The six trend widgets (open/resolved by severity, hosts, hosts by type)
  // are not in the dashboard layout, so their data is not fetched. Restore a
  // widget's layout entry together with its fetch.

  useEffect(() => {
    // The V3 prioritization flow widget fetches its own filter chain.
    if (useVulnerabilityV3) return;
    dispatch(
      actions.getVulnerabilityPrioritizationData({
        q: {
          environmentId,
          accountId: !!accountId ? [accountId] : [],
          steps: activeStepIds as FunnelStep[],
        },
      }),
    );
  }, [dispatch, environmentId, accountId, activeStepIds, useVulnerabilityV3]);

  const availableWidgets1: { [key: string]: WidgetProps } = {
    vulnerabilitiesPrioritization: {
      flex: true,
      components: {
        content: useVulnerabilityV3 ? (
          <PrioritizationFlowWidget setShow={props.setShow} />
        ) : (
          <VulnerabilitiesPrioritization
            setShow={props.setShow}
            activeStepIds={activeStepIds}
            setActiveStepIds={setActiveStepIds}
          />
        ),
      },
    },
    patchablePackages: {
      flex: true,
      components: {
        content: <PatchablePackages setShow={props.setShow} />,
      },
    },
    vulnerabilitiesBySeverity: {
      flex: true,
      components: {
        content: <VulnerabilitiesBySeverity />,
      },
      className: 'vulnerabilitiesBySeverity',
    },
    vulnerabilitiesBySources: {
      flex: true,
      components: {
        content: <VulnerabilitiesBySources />,
      },
      className: 'vulnerabilitiesBySources',
    },
    mostDeployedVulnerableImages: {
      flex: true,
      components: {
        content: <MostDeployedVulnerableImages />,
      },
      className: 'mostDeployedVulnerableImages',
    },
    openVulnerabilitiesBySeverity: {
      flex: true,
      components: {
        content: (
          <VulnerabilitiesBySeverityChart
            severities={openVulnerabilitiesBySeverity}
            chartType={state.vulnerabilityChartType}
            cumulative={
              useVulnerabilityV3 &&
              state.vulnerabilityChartType?.value === 'line'
            }
            updateValue={val => updateState({ vulnerabilityChartType: val })}
          />
        ),
      },
      className: 'criticalAndHighVulnerabilities',
    },
    hostsWithUnpatchedOSAndTechnologyOpen: {
      flex: true,
      components: {
        content: (
          <HostsWithUnpatchedOSAndTechnology
            severities={hostsWithOpenVulnerabilitiesByType}
            chartType={state.technologyChartType}
            cumulative={
              useVulnerabilityV3 && state.technologyChartType?.value === 'line'
            }
            updateValue={val => updateState({ technologyChartType: val })}
          />
        ),
      },
      className: 'hostsWithUnpatchedOSAndTechnologyOpen',
    },
    hostsWithOpenVulnerabilities: {
      flex: true,
      components: {
        content: (
          <HostsWithVulnerabilities
            severities={hostsWithOpenVulnerabilities}
            chartType={state.hostChartType}
            cumulative={
              useVulnerabilityV3 && state.hostChartType?.value === 'line'
            }
            updateValue={val => updateState({ hostChartType: val })}
          />
        ),
      },
      className: 'hostsWithOpenVulnerabilities',
    },
    resolvedVulnerabilitiesBySeverity: {
      flex: true,
      components: {
        content: (
          <VulnerabilitiesBySeverityChart
            showResolved
            severities={resolvedVulnerabilitiesBySeverity}
            chartType={state.resolvedVulnerabilityChartType}
            cumulative={
              useVulnerabilityV3 &&
              state.resolvedVulnerabilityChartType?.value === 'line'
            }
            updateValue={val =>
              updateState({ resolvedVulnerabilityChartType: val })
            }
          />
        ),
      },
      className: 'resolvedVulnerabilitiesBySeverity',
    },
    hostsWithResolvedVulnerabilities: {
      flex: true,
      components: {
        content: (
          <HostsWithVulnerabilities
            severities={hostsWithResolvedVulnerabilities}
            showResolved
            chartType={state.resolvedHostChartType}
            cumulative={
              useVulnerabilityV3 &&
              state.resolvedHostChartType?.value === 'line'
            }
            updateValue={val => updateState({ resolvedHostChartType: val })}
          />
        ),
      },
      className: 'hostsWithResolvedVulnerabilities',
    },
    hostsWithUnpatchedOSAndTechnologyResolved: {
      flex: true,
      components: {
        content: (
          <HostsWithUnpatchedOSAndTechnology
            severities={hostsWithResolvedVulnerabilitiesByType}
            showResolved
            chartType={state.resolvedTechnologyChartType}
            cumulative={
              useVulnerabilityV3 &&
              state.resolvedTechnologyChartType?.value === 'line'
            }
            updateValue={val =>
              updateState({ resolvedTechnologyChartType: val })
            }
          />
        ),
      },
      className: 'hostsWithUnpatchedOSAndTechnologyResolved',
    },
  };

  const dashboardWidgets1: IDashboardLayout = {
    id: 'dashboard_1',
    title: 'Dashboard Title',
    widgets: [
      {
        type: 'funnel-chart',
        i: 'vulnerabilitiesPrioritization',
        x: 0,
        y: 0,
        // The flow needs room for the stage columns and its side panel.
        w: useVulnerabilityV3 ? 8 : 6,
        h: useVulnerabilityV3 ? 12 : 8,
        minH: 4,
        minW: 2,
        isResizable: false,
        static: true,
      },
      {
        type: 'table',
        i: 'patchablePackages',
        x: useVulnerabilityV3 ? 8 : 6,
        y: 0,
        w: useVulnerabilityV3 ? 4 : 6,
        h: useVulnerabilityV3 ? 12 : 8,
        minH: 4,
        minW: 2,
        isResizable: false,
        static: true,
      },
      {
        type: 'pie-chart',
        i: 'vulnerabilitiesBySeverity',
        x: 0,
        y: useVulnerabilityV3 ? 12 : 8,
        w: 4,
        h: 8,
        minH: 2,
        minW: 2,
        isResizable: false,
        static: true,
      },
      {
        type: 'bar-chart',
        i: 'vulnerabilitiesBySources',
        x: 4,
        y: useVulnerabilityV3 ? 12 : 8,
        w: 4,
        h: 8,
        minH: 2,
        minW: 2,
        isResizable: false,
        static: true,
      },
      {
        type: 'sunburst-chart',
        i: 'mostDeployedVulnerableImages',
        x: 8,
        y: useVulnerabilityV3 ? 12 : 8,
        w: 4,
        h: 8,
        minH: 2,
        minW: 2,
        isResizable: false,
        static: true,
      },
      // {
      //   type: 'line-chart',
      //   i: 'openVulnerabilitiesBySeverity',
      //   x: 0,
      //   y: 16,
      //   w: 4,
      //   h: 8,
      //   minH: 4,
      //   minW: 2,
      //   isResizable: false,
      //   static: true,
      // },
      // {
      //   type: 'bar-chart',
      //   i: 'hostsWithOpenVulnerabilities',
      //   x: 4,
      //   y: 16,
      //   w: 4,
      //   h: 8,
      //   isResizable: false,
      //   static: true,
      // },
      // {
      //   type: 'line-chart',
      //   i: 'hostsWithUnpatchedOSAndTechnologyOpen',
      //   x: 8,
      //   y: 16,
      //   w: 4,
      //   h: 8,
      //   minH: 4,
      //   minW: 2,
      //   isResizable: false,
      //   static: true,
      // },
      // {
      //   type: 'line-chart',
      //   i: 'resolvedVulnerabilitiesBySeverity',
      //   x: 0,
      //   y: 24,
      //   w: 4,
      //   h: 8,
      //   minH: 4,
      //   minW: 2,
      //   isResizable: false,
      //   static: true,
      // },
      // {
      //   type: 'bar-chart',
      //   i: 'hostsWithResolvedVulnerabilities',
      //   x: 4,
      //   y: 24,
      //   w: 4,
      //   h: 8,
      //   isResizable: false,
      //   static: true,
      // },
      // {
      //   type: 'line-chart',
      //   i: 'hostsWithUnpatchedOSAndTechnologyResolved',
      //   x: 8,
      //   y: 24,
      //   w: 4,
      //   h: 8,
      //   minH: 4,
      //   minW: 2,
      //   isResizable: false,
      //   static: true,
      // },
    ],
    available: availableWidgets1,
  };

  return (
    <Box w="full" h="full" overflow="scroll">
      <CDashboard dashboardWidgets={dashboardWidgets1} />
    </Box>
  );
};
